import type { LucideIcon } from '../../../icons';
import './EmptyState.css';

/**
 * 빈 상태의 네 가지 형 — 디자인 정본 G1 · G4 (2026-09-15 `kind` 추가).
 *
 * 문구와 **행동**이 형마다 다르다:
 *   - `first-run`     처음이라 비어 있다 — 「만들기」 같은 행동이 있다.
 *   - `no-result`     찾았는데 없다 — 「필터 지우기」는 되지만 **「만들기」는 두지 않는다**
 *                     (사용자는 찾으러 왔지 만들러 온 게 아니다).
 *   - `no-permission` 볼 권한이 없다 — **행동을 두지 않는다**(누를 게 없다). `action` 을 줘도 그리지 않는다.
 *                     그리고 403 은 **그 영역만** 가린다 — 화면 전체를 가리지 않는다.
 *   - `error`         불러오지 못했다 — 「다시 시도」. 스크린리더가 바로 읽도록 `role="alert"`.
 *
 * ★ `kind` 를 안 주면 **종전과 똑같이** 그린다(클래스 · 역할 · 행동 모두) — 기존 호출부 무영향.
 */
export type EmptyStateKind = 'first-run' | 'no-result' | 'no-permission' | 'error';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  kind?: EmptyStateKind;
}

export function EmptyState({ icon: Icon, title, description, action, kind }: EmptyStateProps) {
  const showAction = action && kind !== 'no-permission';
  return (
    <div
      className={kind ? `empty-state empty-state--${kind}` : 'empty-state'}
      role={kind === 'error' ? 'alert' : undefined}
      data-kind={kind}
    >
      {Icon && <Icon size={48} className="empty-state-icon" />}
      <h3 className="empty-state-title">{title}</h3>
      {description && <p className="empty-state-description">{description}</p>}
      {showAction && <div className="empty-state-action">{action}</div>}
    </div>
  );
}
